import { useState, useRef, useEffect } from "react";
import dompurify from 'dompurify';
import { getValidSasToken, fetchSasToken } from "../store/documentsSlice";
import { useAppDispatch } from "../store";
import axios from "axios";
import { getFileName } from "../utils";
import { Spin } from "antd";
import styled from "styled-components";

type Props = {
  html: string;
  inline: string[];
  envConfig: any;
};

const Container = styled.div`
  display: flex;
  justify-content: center;
`;

//sort inline images in order to parse them correcty into email body
const sortImages = (imagesArr) => {
  return [...imagesArr]
    .map((el) => [el.replace("image00", "image").replace("~", ""), el])
    .sort((a, b) => a[0].localeCompare(b[0]))
    ?.map((e) => e[1]);
};

const axiosWithoutAuth = axios.create();

export default function HtmlRenderer({ html, inline, envConfig }: Props) {
  const dispatch = useAppDispatch();
  const [isProcessingImages, setIsProcessingImages] = useState(true);
  const ref = useRef<HTMLDivElement | null>(null);

  // Preprocess the HTML to add placeholder `src` attributes for matching images
  const preprocessHtml = (html: string, inlineImages: string[]) => {
    if (!inlineImages?.length) {
      return html;
    }
    const sanitizedInlineImages = sortImages(inlineImages).map(getFileName); // Get short filenames
    return html.replace(/<img [^>]*src="[^"]*"[^>]*alt="([^"]+)"[^>]*>/g, (match, alt) => {
      if (sanitizedInlineImages.includes(alt)) {
        // Overwrite the existing `src` attribute for matching images
        return match.replace(/src="[^"]*"/, `src="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw=="`);
      }
      return match; // Leave non-matching images unchanged
    });
  };

  const processedHtml = preprocessHtml(html, inline);

  useEffect(() => {
    if (!ref?.current) {
      return;
    }

    if (!inline?.length) {
      setIsProcessingImages(false);
      return;
    }

    const imgs = ref.current.querySelectorAll("img");
    const inlineImages = sortImages(inline);

    if (!inlineImages || !imgs.length) {
      setIsProcessingImages(false);
      return;
    }

    const encodeLink = (link: string, sasToken: string) =>
      `https://${envConfig.appConfig.storageAccountName}.blob.core.windows.net/inline/` +
      encodeURI(link) +
      `?` +
      sasToken;
    
    const fetchAndSetImageSrc = async (img: HTMLImageElement, link: string) => {
      try {
        // Ensure the SAS token is valid
        const validSasToken = await dispatch(getValidSasToken()).unwrap();
        const encodedSrc = encodeLink(link, validSasToken);

        // Fetch the resource and create a Blob URL
        const response = await axiosWithoutAuth.get(encodedSrc, { responseType: "blob" });
        const blobUrl = URL.createObjectURL(response.data);

        img.src = blobUrl;
      } catch (error) {
        console.error("Failed to fetch image:", error);

        // Refresh the SAS token
        const controller = new AbortController();
        const refreshedSasToken = await dispatch(fetchSasToken({ controller })).unwrap();
        controller.abort();

        // Retry with the new token
        const refreshedEncodedSrc = encodeLink(link, refreshedSasToken);
        try {
          const refreshedResponse = await axiosWithoutAuth.get(refreshedEncodedSrc, { responseType: "blob" });
          const refreshedBlobUrl = URL.createObjectURL(refreshedResponse.data);

          img.src = refreshedBlobUrl;
        } catch (retryError) {
          console.error("Failed to fetch image after retry:", retryError);
          img.src = ""; // Clear the image source if all attempts fail
        }
      }
    };

    const processImages = async () => {
      const promises = Array.from(imgs).map(async (img) => {
        const imgAlt = img.getAttribute("alt");
        // Find the corresponding image in inlineImages by matching the short file name
        const matchingImage = inlineImages.find((inlineImage) => {
          const shortFileName = getFileName(inlineImage);
          return imgAlt && shortFileName && imgAlt === shortFileName;
        });

        if (matchingImage) {
          await fetchAndSetImageSrc(img, matchingImage);
        }
      });

      await Promise.all(promises); // Wait for all images to be processed
      setIsProcessingImages(false); // Mark image processing as complete
    };

    processImages();

    // Cleanup Blob URLs when the component unmounts
    return () => {
      if (!imgs) return;
      imgs.forEach((img) => {
        if (img.src.startsWith("blob:")) {
          URL.revokeObjectURL(img.src);
        }
      });
    };
  }, [envConfig, inline, dispatch]);

  return (
    <div ref={ref}>
      {isProcessingImages && (
        <Container>
          <Spin size="large" />
        </Container>
      )}
      {/* Render the preprocessed HTML */}
      <div
        dangerouslySetInnerHTML={{
          __html: dompurify.sanitize(processedHtml),
        }}
      ></div>
    </div>
  );
}
